import { Component, createRef } from 'react';

import { GenericMouseEvent } from '../../../types/types';
import { UIUtils } from '../../../utils/dom-utils';
import { JSUtils } from '../../../utils/js.utils';
import { WindowUtils } from '../../../utils/window.utils';
import style from './range-selector.style.module.scss';
import { OptionalChildrenProps } from '../../../types/react.types';


interface RangeSelectorProps extends OptionalChildrenProps {
    max?: number;
    min?: number;
    start?: number;
    end?: number;
    step?: number;
    valueFormatter?: (value: any) => string;
    onChange?: (value: RangeSelectorResult) => void;
    onChangeEnd?: (value: RangeSelectorResult) => void;
}

interface RangeSelectorState {
    start: number;
    end: number;
    left: number;
    right: number;
}

export type RangeHandlerType = 'start' | 'end';
export class RangeHandlerTypes {
    public static START: RangeHandlerType = 'start';
    public static END: RangeHandlerType = 'end';
}


export interface RangeSelectorResult {
    handlerType: RangeHandlerType;
    start: number;
    end: number;
}

export default class RangeSelector extends Component<RangeSelectorProps, RangeSelectorState> {
    container = createRef<HTMLDivElement>();
    handler: ((e: GenericMouseEvent) => void) | null = null;
    eventOffset = 0; // Need to set this to 0 if manually uploading

    startHandler: HTMLElement | null = null;
    endHandler: HTMLElement | null = null;
    isStartHandler: boolean = false;

    max = 100;
    min = 0;
    step = 1;

    valueFormatter = (val: number) => val;

    readonly state: Readonly<RangeSelectorState> = {
        start: this.props?.start ?? 0,
        end: this.props?.end ?? 100,
        left: this.props?.start ?? 0,
        right: this.props?.end ?? 100,
    };
    constructor(props: RangeSelectorProps) {
        super(props);
        this.max = this.props.max || 100;
        this.min = this.props.min || 0;
        this.step = this.props.step || 1;

        const stepStr = `${this.step}`;
        if (stepStr.indexOf('.') > -1) {
            const numberOfDecimals = stepStr.length - (stepStr.indexOf('.') + 1);
            this.valueFormatter = (val) => {
                const parsedVal = val.toFixed(numberOfDecimals);
                return Number(parsedVal);
            };
        }
    }

    componentDidMount() {
        JSUtils.runMacroTask(() => {
            this.startHandler = this.container.current.querySelector(`.${style.start}`) as HTMLElement;
            this.endHandler = this.container.current.querySelector(`.${style.end}`) as HTMLElement;

            const rail: HTMLElement = this.container.current.querySelector(`.${style.rail}`) as HTMLElement;
            const railRect = rail.getBoundingClientRect();

            this.isStartHandler = false;
            const initialEndPosition = (railRect.left - 9) + railRect.width * ((this.props.end || this.max) / (this.max + this.min));
            this.updateHandlersPositionAndNotify(initialEndPosition, false);

            this.isStartHandler = true;
            const initialStartPosition = (railRect.left - 9) + railRect.width * ((this.props.start || this.min) / (this.max + this.min));
            this.updateHandlersPositionAndNotify(initialStartPosition, false);
            this.forceUpdate();
        });
    }

    handleMousedown(e: MouseEvent | any) {
        if (e.target instanceof HTMLElement) {
            this.startHandler = this.container.current.querySelector(`.${style.start}`) as HTMLElement;
            this.endHandler = this.container.current.querySelector(`.${style.end}`) as HTMLElement;
            this.isStartHandler = e.target.classList.contains(`${style.start}`);
            this.handler = (ev: any) => this.handleMouseMove(ev);
            const event = UIUtils.toGenericMouseEvent(e);
            if (event) {
                this.eventOffset = event.offsetX;
                WindowUtils.addMoveMoveHandlerToWindow(this.handler, true);
            }
        }
    }

    handleMouseMove(event: GenericMouseEvent) {
        if (event.type === 'touchend' || event.type === 'mouseup') {
            this.handler = null;
            return;
        }
        if (event) {
            this.updateHandlersPositionAndNotify(event.pageX);
        }
    }

    private updateHandlersPositionAndNotify(pageX: number, notify = true) {
        if (!this.startHandler || !this.endHandler) {
            return;
        }

        const startHandlerRect = this.startHandler.getBoundingClientRect();
        const endHandlerRect = this.endHandler.getBoundingClientRect();

        const rail: HTMLElement = this.container.current.querySelector(`.${style.rail}`) as HTMLElement;
        const railRect = rail.getBoundingClientRect();

        let handlerLeft = pageX - (railRect.left + this.eventOffset);

        if (this.isStartHandler) {
            const handlerX = railRect.left + handlerLeft;
            if (handlerLeft < -9) {
                handlerLeft = -9;
            } else if (handlerX > endHandlerRect.left - 1) {
                // start handler crossed end handlers center
                handlerLeft = endHandlerRect.left - (railRect.left - 1);
            }
        } else {
            const handlerX = railRect.left + handlerLeft;
            if (handlerLeft > railRect.width - 9) {
                handlerLeft = railRect.width - 9;
            } else if (handlerX < startHandlerRect.left + 1) {
                // start handler crossed end handlers center
                handlerLeft = startHandlerRect.left - (railRect.left - 1);
            }
        }
        const absSeekPercentage = (handlerLeft) / railRect.width;
        const seekPercentage = (handlerLeft + 9) / railRect.width;
        const readablePosition = seekPercentage * (this.max - this.min) + this.min;

        const offsetFromClosestLowerStep = (readablePosition - this.min) % this.step;
        const offsetToClosestStep = offsetFromClosestLowerStep >= this.step / 2 ?
            this.step - offsetFromClosestLowerStep : -1 * offsetFromClosestLowerStep;

        let position = this.valueFormatter(readablePosition + offsetToClosestStep);

        if (this.isStartHandler) {
            this.startHandler.style.left = `${absSeekPercentage * 100}%`; // `${handlerLeft}px`;
            this.setState({
                start: position,
                left: handlerLeft,
            });
        } else {
            this.endHandler.style.left = `${absSeekPercentage * 100}%`; // `${handlerLeft}px`;
            this.setState({
                end: position,
                right: handlerLeft,
            });
        }

        const bridgeEl = this.container.current.querySelector<HTMLDivElement>(`.${style.bridge}`);
        if (bridgeEl) {
            bridgeEl.style.left = this.startHandler.style.left;
            bridgeEl.style.right = `calc(100% - ${this.endHandler.style.left})`;
        }


        if (this.props.onChange && notify) {
            this.props.onChange({
                handlerType: this.isStartHandler ? RangeHandlerTypes.START : RangeHandlerTypes.END,
                start: this.isStartHandler ? position : this.state.start,
                end: this.isStartHandler ? this.state.end : position,
            });
        }
        if (this.props.onChangeEnd && notify) {
            this.props.onChangeEnd({
                handlerType: this.isStartHandler ? RangeHandlerTypes.START : RangeHandlerTypes.END,
                start: this.isStartHandler ? position : this.state.start,
                end: this.isStartHandler ? this.state.end : position,
            });
        }
    }

    componentWillUpdate(next) {
        if (!this.handler) {
            this.eventOffset = 0;
            if (this.props.start !== next.start || this.props.end !== next.end) {
                console.error('React migrate');
                // this.props.start = next.start;
                // this.props.end = next.end;
                this.componentDidMount();
            }
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const shouldRenderProps = !this.handler && (this.props.start !== nextProps.start || this.props.end !== nextProps.end);
        const shouldRenderState = nextState && nextState.start !== this.state.start || nextState.end !== this.state.end
        return shouldRenderProps || shouldRenderState;
    }

    render() {
        return (
            <div ref={this.container} className={style.container}>
                <div className={style.state}>
                    <div className={style.min}>{this.state.start}</div>
                    <div className={style.value}> </div>
                    <div className={style.max}>{this.state.end}</div>
                </div>
                <div className={style['range-selector']}>
                    <div className={style.rail}>
                        <span className={style.bridge}></span>
                        <span className={`${style.handler} ${style.start}`}
                            onTouchStart={(e) => this.handleMousedown(e)}
                            onMouseDown={(e) => this.handleMousedown(e)}
                        ></span>
                        <span className={`${style.handler} ${style.end}`}
                            onTouchStart={(e) => this.handleMousedown(e)}
                            onMouseDown={(e) => this.handleMousedown(e)}
                        ></span>
                    </div>
                </div>
            </div>
        );
    }
}